import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { usePageTitle } from "@/lib/seo";
import { Reveal } from "@/components/Reveal";
import { CTASection } from "@/components/CTASection";
import { API } from "@/lib/api";

export default function BlogArticle() {
  const { slug } = useParams();
  const { t, lang } = useLanguage();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  usePageTitle(article ? `${article.title} | Scutul Fermierului` : t.meta.blog);

  useEffect(() => {
    setLoading(true);
    setArticle(null);
    axios
      .get(`${API}/articles/${slug}`, { params: { lang } })
      .then((r) => setArticle(r.data))
      .catch(() => setArticle(null))
      .finally(() => setLoading(false));
  }, [slug, lang]);

  return (
    <div data-testid={`blog-article-${slug}`} className="pt-24">
      {loading ? (
        <div className="mx-auto max-w-3xl animate-pulse px-6 py-24">
          <div className="h-10 w-3/4 bg-sand" />
          <div className="mt-6 h-4 w-full bg-sand" />
          <div className="mt-3 h-4 w-full bg-sand" />
        </div>
      ) : !article ? (
        <div className="mx-auto max-w-3xl px-6 py-24 text-center">
          <p data-testid="article-not-found" className="font-serif text-3xl text-ink">{t.blog.notFound}</p>
          <Link data-testid="article-back-notfound" to="/blog" className="mt-6 inline-block text-bronze underline underline-offset-4">
            {t.blog.back}
          </Link>
        </div>
      ) : (
        <>
          <section className="relative overflow-hidden">
            <div className="absolute inset-0">
              <img src={article.image} alt={article.title} className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/60 to-ink/20" />
            </div>
            <div className="relative mx-auto max-w-4xl px-6 pb-20 pt-20 md:pt-28">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              >
                <Link
                  data-testid="article-back-link"
                  to="/blog"
                  className="group inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-ivory/60 transition-colors hover:text-bronze"
                >
                  <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
                  {t.blog.back}
                </Link>
                <p className="mt-10 text-xs uppercase tracking-[0.25em] text-bronze md:text-sm">
                  {article.category} · {article.date} · {article.read_minutes} {t.blogSection.minRead}
                </p>
                <h1 className="mt-6 font-serif text-4xl leading-[1.02] tracking-tight text-ivory md:text-5xl lg:text-6xl">
                  {article.title}
                </h1>
              </motion.div>
            </div>
          </section>

          <article className="mx-auto max-w-3xl px-6 py-16 md:py-24">
            <Reveal>
              <p className="font-serif text-2xl leading-snug text-ink/90 md:text-3xl">{article.excerpt}</p>
            </Reveal>
            {article.content.map((block, i) => (
              <Reveal key={i} delay={0.05}>
                <div className="mt-12">
                  <h2 className="flex items-baseline gap-4 font-serif text-3xl text-ink md:text-4xl">
                    <span className="text-lg text-bronze/50">0{i + 1}</span>
                    {block.h}
                  </h2>
                  <p className="mt-5 text-base leading-relaxed text-stone md:text-lg">{block.p}</p>
                </div>
              </Reveal>
            ))}
          </article>
        </>
      )}
      <CTASection />
    </div>
  );
}
